import React, { useState, useEffect } from 'react';
import { MessageCircle, Mail, Globe, Share2, Palette, ChevronDown, Menu, X, Check, Star } from 'lucide-react';

// ==================== CONFIG ====================
const CONTACT = {
  whatsapp: '0744911898',
  whatsappLink: 'https://wa.me/27744911898',
  email: 'mduduzigabuza9@gmail.com',
  defaultMessage: "Hi BrandLift 👋 I'm interested in: (Website / Social Media / Branding). My business is ___. My budget is ___. Please contact me."
};

// ==================== PRICING DATA ====================
const WEBSITE_PACKAGES = [
  {
    id: 'basic-website',
    name: 'Basic Website',
    price: 'R4,000',
    idealFor: 'Startups and small businesses',
    features: [
      '1–3 page website',
      'Mobile-friendly design',
      'Contact information and enquiry form',
      'Clean, professional layout'
    ],
    maintenance: 'R500 p/m — Basic Maintenance',
    recommended: true
  },
  {
    id: 'standard-website',
    name: 'Standard Website',
    price: 'R6,000',
    idealFor: 'Growing businesses',
    features: [
      '4–5 page website',
      'Improved design and structure',
      'Mobile responsiveness',
      'Contact/enquiry forms',
      'Basic SEO setup'
    ],
    maintenance: 'R700 p/m — Standard Maintenance'
  },
  {
    id: 'professional-website',
    name: 'Professional Website',
    price: 'R8,000',
    idealFor: 'Established businesses',
    features: [
      '5–7 page website',
      'Custom design aligned with your brand',
      'Mobile optimisation',
      'Image galleries or additional sections',
      'Basic SEO optimisation'
    ],
    maintenance: 'R1,000 p/m — Professional Maintenance',
    popular: true
  },
  {
    id: 'advanced-website',
    name: 'Advanced Website',
    price: 'R10,000',
    idealFor: 'Additional functionality',
    features: [
      '7+ pages',
      'Advanced features (custom forms, integrations, booking)',
      'Enhanced design and layout',
      'Mobile optimisation',
      'Enhanced SEO setup'
    ],
    maintenance: 'R1,200 p/m — Advanced Maintenance'
  },
  {
    id: 'custom-website',
    name: 'Custom Website',
    price: 'R10,000+',
    priceNote: "Let's Talk",
    idealFor: 'Larger/complex businesses',
    features: [
      'Fully custom website solution',
      'Complex functionality and integrations',
      'Tailored design and structure',
      'Scope discussed and quoted accordingly'
    ],
    maintenance: 'Quoted based on requirements'
  }
];

const SOCIAL_SETUP = [
  {
    id: 'basic-setup',
    name: 'Basic Setup',
    price: 'R800',
    features: ['1 platform', 'Profile optimisation', 'Bio setup']
  },
  {
    id: 'standard-setup',
    name: 'Standard Setup',
    price: 'R1,200',
    features: ['Up to 2 platforms', 'Bio writing', 'Profile optimisation'],
    popular: true
  },
  {
    id: 'professional-setup',
    name: 'Professional Setup',
    price: 'R1,500',
    features: [
      'Up to 3 platforms',
      'Bio writing',
      'Profile optimisation',
      'Highlight covers and basic structure'
    ]
  }
];

const SOCIAL_MANAGEMENT = [
  {
    id: 'starter-package',
    name: 'Starter Package',
    price: 'R2,000 p/m',
    features: ['Basic posting and page management']
  },
  {
    id: 'growth-package',
    name: 'Growth Package',
    price: 'R3,500 p/m',
    features: ['Increased posting frequency', 'Engagement and content planning'],
    popular: true
  },
  {
    id: 'premium-package',
    name: 'Premium Package',
    price: 'R5,000 p/m',
    features: [
      'High quality content strategy',
      'Regular posting and active engagement'
    ]
  }
];

const BRANDING_PACKAGES = [
  {
    id: 'basic-branding',
    name: 'Basic Branding',
    price: 'R2,500',
    idealFor: 'Startups',
    features: [
      'Logo design',
      'Brand colours & font selection',
      'Basic brand direction'
    ]
  },
  {
    id: 'standard-branding',
    name: 'Standard Branding',
    price: 'R3,200',
    idealFor: 'Growing businesses',
    features: [
      'Logo design',
      'Brand colours & fonts',
      'Social media–ready logo versions',
      'Basic brand consistency guidelines'
    ],
    popular: true
  },
  {
    id: 'full-branding',
    name: 'Full Branding Package',
    price: 'R4,000',
    idealFor: 'Established businesses',
    features: [
      'Logo design',
      'Brand colours & fonts',
      'Brand guide (usage & consistency)',
      'Social media & digital-ready assets'
    ]
  }
];

const TESTIMONIAL = {
  text: "I would like to sincerely recommend Brandlifts marketing for their outstanding work in creating and managing my company website for Mathematise Tutoring. As the founder of the company, I, Tokollo Mahlatse Malatji, am extremely impressed with the professionalism, creativity, and consistency they have shown throughout the entire process.\n\nFrom the initial design to the ongoing weekly management, they have ensured that our website remains modern, functional, and user-friendly. They took the time to understand my vision for Mathematise Tutoring and transformed it into a website that truly reflects the brand's values of excellence in Maths and Sciences education.\n\nThe weekly management has been reliable and efficient, keeping the website updated, responsive, and running smoothly at all times. What stands out most is their attention to detail, quick communication, and dedication to delivering high-quality work.\n\nI highly recommend them to anyone looking for a skilled and dependable website developer and manager. Their work has added great value to my business and strengthened our online presence.",
  author: "Tokollo Mahlatse Malatji",
  position: "Founder, Mathematise Tutoring",
  websiteUrl: "https://mathematise.co.za/"
};

const LOGO_WORK = [
  { src: '/logo/1.png', alt: 'Logo design sample 1' },
  { src: '/logo/2.png', alt: 'Logo design sample 2' },
  { src: '/logo/3.png', alt: 'Logo design sample 3' },
  { src: '/logo/4.png', alt: 'Logo design sample 4' },
  { src: '/logo/5.png', alt: 'Logo design sample 5' }
];

const FAQS = [
  {
    q: "What's included in website maintenance?",
    a: "Website maintenance includes regular updates and backups, security checks, minor content updates, and ongoing technical support to keep your site running smoothly."
  },
  {
    q: "How long does a website take?",
    a: "Typical turnaround is 1-3 weeks depending on the package and complexity. We'll provide a clear timeline once we understand your requirements."
  },
  {
    q: "Can you redesign my current website?",
    a: "Absolutely! We can work with your existing site or build from scratch. Message us on WhatsApp to discuss your current setup."
  },
  {
    q: "What platforms do you manage?",
    a: "We manage all major social platforms including Facebook, Instagram, LinkedIn, Twitter/X, TikTok, and more based on where your audience is."
  },
  {
    q: "Do you provide content and captions?",
    a: "Yes! Our social media management packages include content creation, caption writing, and strategic planning tailored to your brand."
  },
  {
    q: "Can I start with setup only?",
    a: "Definitely! Many clients start with our setup packages and add management later as their business grows."
  },
  {
    q: "What's the difference between branding and logo-only?",
    a: "A logo is just one element. Branding includes your complete visual identity—logo, colors, typography, usage guidelines—so everything looks consistent everywhere."
  },
  {
    q: "What payment methods do you accept?",
    a: "We accept bank transfers (EFT) and card payments. A 50% deposit is required to commence work, with the remaining 50% due before launch."
  },
  {
    q: "Do you offer month-to-month?",
    a: "Yes, our maintenance and management packages are billed monthly with no long-term contracts required."
  },
  {
    q: "How do we begin?",
    a: "Simply message us on WhatsApp or email. We'll discuss your needs, recommend the best package, and get started!"
  }
];

// ==================== MAIN COMPONENT ====================
export default function BrandLiftLanding() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [expandedFaq, setExpandedFaq] = useState(null);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const whatsappUrl = (message = CONTACT.defaultMessage) => {
    return `${CONTACT.whatsappLink}?text=${encodeURIComponent(message)}`;
  };

  const emailUrl = () => {
    const subject = "BrandLift Marketing Enquiry";
    const body = "Hi BrandLift,\n\nI'm interested in your services.\n\nMy business: ___\nService needed: ___\nBudget: ___\n\nPlease contact me.\n\nThank you!";
    return `mailto:${CONTACT.email}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
  };

  return (
    <div className="min-h-screen bg-[#fcfdfd] text-[#595d72]" style={{ fontFamily: "'Poppins', sans-serif" }}>
      {/* Sticky Header */}
      <header className={`fixed top-0 w-full z-50 transition-all duration-300 ${scrolled ? 'bg-[#152854]/95 backdrop-blur-md shadow-lg' : 'bg-[#152854]'}`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-20">
            {/* Brand logo */}
            <a href="#hero" className="inline-flex items-center" aria-label="BrandLift Marketing home">
              <img src="/brandlift-logo.jpg" alt="BrandLift Marketing" className="h-14 w-auto max-w-[180px] object-contain" />
            </a>

            {/* Desktop Nav */}
            <nav className="hidden md:flex items-center space-x-6 text-sm">
              <a href="#services" className="text-white/90 hover:text-[#dca851] transition">Services</a>
              <a href="#pricing" className="text-white/90 hover:text-[#dca851] transition">Pricing</a>
              <a href="#reviews" className="text-white/90 hover:text-[#dca851] transition">Reviews</a>
              <a href="#faq" className="text-white/90 hover:text-[#dca851] transition">FAQ</a>
            </nav>

            {/* CTA Buttons */}
            <div className="hidden md:flex items-center space-x-3">
              <a href={emailUrl()} className="px-4 py-2 border-2 border-[#dca851] text-white rounded-lg hover:bg-[#dca851]/10 transition font-medium text-sm">
                Email Us
              </a>
              <a href={whatsappUrl()} target="_blank" rel="noopener noreferrer" className="px-5 py-2.5 bg-[#dca851] text-[#152854] rounded-lg hover:bg-[#d7ab61] transition font-bold text-sm flex items-center space-x-2 shadow-lg">
                <MessageCircle size={18} />
                <span>WhatsApp Us</span>
              </a>
            </div>

            {/* Mobile Menu Button */}
            <button onClick={() => setMobileMenuOpen(!mobileMenuOpen)} className="md:hidden text-white p-2">
              {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="md:hidden bg-[#17294f] border-t border-white/10">
            <div className="px-4 py-4 space-y-3">
              <a href="#services" onClick={() => setMobileMenuOpen(false)} className="block text-white py-2">Services</a>
              <a href="#pricing" onClick={() => setMobileMenuOpen(false)} className="block text-white py-2">Pricing</a>
              <a href="#reviews" onClick={() => setMobileMenuOpen(false)} className="block text-white py-2">Reviews</a>
              <a href="#faq" onClick={() => setMobileMenuOpen(false)} className="block text-white py-2">FAQ</a>
              <div className="pt-3 space-y-2">
                <a href={emailUrl()} className="block w-full px-4 py-2.5 border-2 border-[#dca851] text-white rounded-lg text-center">
                  Email Us
                </a>
                <a href={whatsappUrl()} target="_blank" rel="noopener noreferrer" className="block w-full px-4 py-2.5 bg-[#dca851] text-[#152854] rounded-lg text-center font-bold">
                  WhatsApp Us
                </a>
              </div>
            </div>
          </div>
        )}
      </header>

      {/* Hero Section */}
      <section id="hero" className="relative pt-32 pb-20 px-4 overflow-hidden">
        {/* Background with overlay */}
        <div className="absolute inset-0 bg-gradient-to-br from-[#152854] via-[#17294f] to-[#152854] opacity-95"></div>
        <div className="absolute inset-0 opacity-10" style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23dca851' fill-opacity='1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`
        }}></div>
        
        <div className="relative max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <div className="inline-block mb-6 animate-pulse">
              <span className="px-4 py-2 bg-[#dca851] text-[#152854] rounded-full text-sm font-bold">
                🚀 Fast Response • Clear Packages • Small-Business Friendly
              </span>
            </div>
            
            <h1 className="text-4xl md:text-6xl lg:text-7xl font-bold text-white mb-6 leading-tight">
              Affordable <span className="text-[#dca851]">Marketing Solutions</span><br />
              for Small Businesses
            </h1>
            
            <p className="text-xl md:text-2xl text-white/90 mb-8 max-w-3xl mx-auto leading-relaxed">
              Social media, websites, and brand strategy—done professionally, without the agency price.
            </p>

            {/* Hero Bullets */}
            <div className="grid md:grid-cols-3 gap-4 max-w-4xl mx-auto mb-10">
              {[
                { icon: Share2, text: 'Social Media Setup & Management' },
                { icon: Globe, text: 'Website Design & Maintenance' },
                { icon: Palette, text: 'Branding & Logo Design' }
              ].map((item, i) => (
                <div key={i} className="flex items-center justify-center space-x-2 text-white/90 bg-white/10 backdrop-blur-sm rounded-lg p-4 border border-white/20">
                  <item.icon className="text-[#dca851]" size={20} />
                  <span className="text-sm font-medium">{item.text}</span>
                </div>
              ))}
            </div>

            {/* CTAs */}
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
              <a href={whatsappUrl()} target="_blank" rel="noopener noreferrer" className="w-full sm:w-auto px-8 py-4 bg-[#dca851] text-[#152854] rounded-lg hover:bg-[#d7ab61] transition font-bold text-lg flex items-center justify-center space-x-2 shadow-2xl transform hover:scale-105">
                <MessageCircle size={24} />
                <span>WhatsApp for a Quote</span>
              </a>
              <a href="#pricing" className="w-full sm:w-auto px-8 py-4 border-2 border-white text-white rounded-lg hover:bg-white/10 transition font-semibold text-lg">
                View Packages
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Services Overview */}
      <section id="services" className="py-16 px-4 bg-white">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-[#152854] mb-4">What We Do</h2>
            <div className="w-24 h-1 bg-[#dca851] mx-auto"></div>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {[
              {
                icon: Share2,
                title: 'Social Media',
                subtitle: 'Setup + Management',
                description: 'Get your brand active and engaging on the platforms that matter to your customers.',
                color: 'from-blue-500 to-purple-500'
              },
              {
                icon: Globe,
                title: 'Websites',
                subtitle: 'Build + Maintenance',
                description: 'Professional, mobile-friendly websites that convert visitors into customers.',
                color: 'from-emerald-500 to-teal-500'
              },
              {
                icon: Palette,
                title: 'Branding',
                subtitle: 'Logo + Full Brand Kit',
                description: 'Complete visual identity that makes your business memorable and consistent.',
                color: 'from-amber-500 to-orange-500'
              }
            ].map((service, i) => (
              <div key={i} className="group bg-[#f5f1f6] rounded-2xl p-8 hover:shadow-xl transition-all duration-300 border-2 border-transparent hover:border-[#dca851]">
                <div className={`w-16 h-16 bg-gradient-to-br ${service.color} rounded-xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform`}>
                  <service.icon className="text-white" size={32} />
                </div>
                <h3 className="text-2xl font-bold text-[#152854] mb-2">{service.title}</h3>
                <p className="text-sm text-[#dca851] font-semibold mb-3">{service.subtitle}</p>
                <p className="text-[#595d72] mb-4 leading-relaxed">{service.description}</p>
                <a href={whatsappUrl(`Hi! I'm interested in ${service.title} services.`)} target="_blank" rel="noopener noreferrer" className="text-[#152854] font-semibold hover:text-[#dca851] transition flex items-center space-x-1">
                  <span>Ask on WhatsApp</span>
                  <ChevronDown className="rotate-[-90deg]" size={16} />
                </a>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Logo Work */}
      <section className="py-16 px-4 bg-[#f5f1f6]">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-10">
            <h2 className="text-3xl md:text-4xl font-bold text-[#152854] mb-3">Proof of Our Work</h2>
            <p className="text-[#595d72]">Recent concepts designed for client brands.</p>
            <div className="w-24 h-1 bg-[#dca851] mx-auto mt-5"></div>
          </div>

          <div className="grid sm:grid-cols-2 gap-6 mb-10">
            {[
              { name: 'Mathematise', url: 'https://mathematise.co.za/', domain: 'mathematise.co.za' },
              { name: 'Eye of Afrika', url: 'https://eyeofafrika.com/', domain: 'eyeofafrika.com' },
              { name: 'Transportify', url: 'https://transportify.co.za/', domain: 'transportify.co.za' }
            ].map((site) => (
              <a
                key={site.url}
                href={site.url}
                target="_blank"
                rel="noopener noreferrer"
                className="group bg-white rounded-2xl border border-[#dca851]/20 p-6 shadow-sm hover:shadow-lg transition flex items-center justify-between gap-4"
              >
                <div>
                  <p className="text-xl font-bold text-[#152854] mb-1">{site.name}</p>
                  <p className="text-[#595d72]">{site.domain}</p>
                </div>
                <Globe className="text-[#dca851] group-hover:scale-110 transition-transform shrink-0" size={28} aria-hidden="true" />
              </a>
            ))}
          </div>

          <div className="mb-10 bg-white rounded-2xl border border-[#dca851]/20 p-4 md:p-6 shadow-sm">
            <p className="text-[#152854] font-semibold mb-3 text-center md:text-left">Mathematise Website Showcase</p>
            <video
              src="/videos/mathematise-showcase.mp4"
              className="w-full rounded-xl bg-black"
              controls
              autoPlay
              muted
              loop
              playsInline
            >
              Your browser does not support the video tags.
            </video>
          </div>

          <div className="grid sm:grid-cols-2 gap-6">
            {LOGO_WORK.map((logo) => (
              <div key={logo.src} className="bg-white rounded-2xl border border-[#dca851]/20 p-4 shadow-sm hover:shadow-lg transition">
                <img src={logo.src} alt={logo.alt} className="w-full h-[280px] object-contain rounded-xl bg-white" loading="lazy" />
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section id="pricing" className="py-20 px-4 bg-gradient-to-b from-[#f5f1f6] to-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-[#152854] mb-4">Transparent Pricing</h2>
            <p className="text-lg text-[#595d72] max-w-2xl mx-auto">No hidden fees. No surprises. Just clear packages designed for small businesses.</p>
            <div className="w-24 h-1 bg-[#dca851] mx-auto mt-6"></div>
          </div>

          {/* Website Packages */}
          <div className="mb-20">
            <h3 className="text-3xl font-bold text-[#152854] mb-8 text-center">Website Packages <span className="text-lg font-normal text-[#595d72]">(Once-Off)</span></h3>
            
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
              {WEBSITE_PACKAGES.map((pkg, i) => (
                <div key={pkg.id} className={`bg-white rounded-2xl shadow-lg overflow-hidden border-2 ${pkg.popular ? 'border-[#dca851] relative' : 'border-gray-100'} hover:shadow-2xl transition-all duration-300`}>
                  {pkg.popular && (
                    <div className="absolute top-0 right-0 bg-[#dca851] text-[#152854] px-4 py-1 rounded-bl-lg font-bold text-sm">
                      POPULAR
                    </div>
                  )}
                  {pkg.recommended && (
                    <div className="absolute top-0 right-0 bg-emerald-500 text-white px-4 py-1 rounded-bl-lg font-bold text-sm">
                      STARTER
                    </div>
                  )}
                  <div className="p-8">
                    <h4 className="text-2xl font-bold text-[#152854] mb-2">{pkg.name}</h4>
                    <div className="mb-4">
                      <span className="text-4xl font-bold text-[#dca851]">{pkg.price}</span>
                      {pkg.priceNote && <span className="text-lg text-[#595d72] ml-2">({pkg.priceNote})</span>}
                    </div>
                    <p className="text-sm text-[#595d72] mb-6 italic">Ideal for {pkg.idealFor}</p>
                    
                    <div className="space-y-3 mb-6">
                      {pkg.features.map((feature, j) => (
                        <div key={j} className="flex items-start space-x-2">
                          <Check className="text-[#dca851] flex-shrink-0 mt-0.5" size={18} />
                          <span className="text-sm text-[#595d72]">{feature}</span>
                        </div>
                      ))}
                    </div>

                    <div className="bg-[#f5f1f6] rounded-lg p-4 mb-6">
                      <p className="text-xs font-semibold text-[#152854] mb-1">Recommended Maintenance:</p>
                      <p className="text-sm text-[#595d72]">{pkg.maintenance}</p>
                    </div>

                    <a href={whatsappUrl(`Hi! I'm interested in the ${pkg.name} package.`)} target="_blank" rel="noopener noreferrer" className="block w-full py-3 bg-[#152854] text-white text-center rounded-lg hover:bg-[#17294f] transition font-semibold">
                      Get Started
                    </a>
                  </div>
                </div>
              ))}
            </div>

            {/* Maintenance Info & Payment Terms */}
            <div className="grid md:grid-cols-2 gap-6">
              <div className="bg-blue-50 border-2 border-blue-200 rounded-xl p-6">
                <h4 className="font-bold text-[#152854] mb-3 flex items-center space-x-2">
                  <Globe size={20} className="text-blue-600" />
                  <span>What Maintenance Includes</span>
                </h4>
                <ul className="space-y-2 text-sm text-[#595d72]">
                  <li className="flex items-start space-x-2">
                    <Check className="text-blue-600 flex-shrink-0 mt-0.5" size={16} />
                    <span>Website updates and backups</span>
                  </li>
                  <li className="flex items-start space-x-2">
                    <Check className="text-blue-600 flex-shrink-0 mt-0.5" size={16} />
                    <span>Security checks</span>
                  </li>
                  <li className="flex items-start space-x-2">
                    <Check className="text-blue-600 flex-shrink-0 mt-0.5" size={16} />
                    <span>Minor content updates</span>
                  </li>
                  <li className="flex items-start space-x-2">
                    <Check className="text-blue-600 flex-shrink-0 mt-0.5" size={16} />
                    <span>Ongoing technical support</span>
                  </li>
                </ul>
              </div>

              <div className="bg-amber-50 border-2 border-[#dca851] rounded-xl p-6">
                <h4 className="font-bold text-[#152854] mb-3 flex items-center space-x-2">
                  <span className="text-[#dca851] text-2xl">💳</span>
                  <span>Payment Terms</span>
                </h4>
                <ul className="space-y-2 text-sm text-[#595d72]">
                  <li className="flex items-start space-x-2">
                    <span className="text-[#dca851] font-bold">•</span>
                    <span><strong>50% deposit</strong> to commence work</span>
                  </li>
                  <li className="flex items-start space-x-2">
                    <span className="text-[#dca851] font-bold">•</span>
                    <span><strong>50% before</strong> the website goes live</span>
                  </li>
                  <li className="flex items-start space-x-2">
                    <span className="text-[#dca851] font-bold">•</span>
                    <span><strong>Monthly maintenance</strong> billed upfront</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>

          {/* Social Media Setup */}
          <div className="mb-20">
            <h3 className="text-3xl font-bold text-[#152854] mb-8 text-center">Social Media Setup <span className="text-lg font-normal text-[#595d72]">(Once-Off)</span></h3>
            
            <div className="grid md:grid-cols-3 gap-6">
              {SOCIAL_SETUP.map((pkg) => (
                <div key={pkg.id} className={`bg-white rounded-2xl shadow-lg p-8 border-2 ${pkg.popular ? 'border-[#dca851]' : 'border-gray-100'} hover:shadow-2xl transition-all duration-300 relative`}>
                  {pkg.popular && (
                    <div className="absolute top-0 right-0 bg-[#dca851] text-[#152854] px-4 py-1 rounded-bl-lg font-bold text-sm">
                      POPULAR
                    </div>
                  )}
                  <h4 className="text-2xl font-bold text-[#152854] mb-2">{pkg.name}</h4>
                  <div className="mb-6">
                    <span className="text-4xl font-bold text-[#dca851]">{pkg.price}</span>
                  </div>
                  
                  <div className="space-y-3 mb-6">
                    {pkg.features.map((feature, j) => (
                      <div key={j} className="flex items-start space-x-2">
                        <Check className="text-[#dca851] flex-shrink-0 mt-0.5" size={18} />
                        <span className="text-sm text-[#595d72]">{feature}</span>
                      </div>
                    ))}
                  </div>

                  <a href={whatsappUrl(`Hi! I'm interested in the ${pkg.name} package.`)} target="_blank" rel="noopener noreferrer" className="block w-full py-3 bg-[#152854] text-white text-center rounded-lg hover:bg-[#17294f] transition font-semibold">
                    WhatsApp to Start
                  </a>
                </div>
              ))}
            </div>
          </div>

          {/* Social Media Management */}
          <div className="mb-20">
            <h3 className="text-3xl font-bold text-[#152854] mb-8 text-center">Social Media Management <span className="text-lg font-normal text-[#595d72]">(Monthly)</span></h3>
            
            <div className="grid md:grid-cols-3 gap-6 mb-6">
              {SOCIAL_MANAGEMENT.map((pkg) => (
                <div key={pkg.id} className={`bg-white rounded-2xl shadow-lg p-8 border-2 ${pkg.popular ? 'border-[#dca851]' : 'border-gray-100'} hover:shadow-2xl transition-all duration-300 relative`}>
                  {pkg.popular && (
                    <div className="absolute top-0 right-0 bg-[#dca851] text-[#152854] px-4 py-1 rounded-bl-lg font-bold text-sm">
                      POPULAR
                    </div>
                  )}
                  <h4 className="text-2xl font-bold text-[#152854] mb-2">{pkg.name}</h4>
                  <div className="mb-6">
                    <span className="text-4xl font-bold text-[#dca851]">{pkg.price}</span>
                  </div>
                  
                  <div className="space-y-3 mb-6">
                    {pkg.features.map((feature, j) => (
                      <div key={j} className="flex items-start space-x-2">
                        <Check className="text-[#dca851] flex-shrink-0 mt-0.5" size={18} />
                        <span className="text-sm text-[#595d72]">{feature}</span>
                      </div>
                    ))}
                  </div>

                  <a href={whatsappUrl(`Hi! I'm interested in the ${pkg.name}.`)} target="_blank" rel="noopener noreferrer" className="block w-full py-3 bg-[#152854] text-white text-center rounded-lg hover:bg-[#17294f] transition font-semibold">
                    Get Started
                  </a>
                </div>
              ))}
            </div>

            <div className="bg-blue-50 border-l-4 border-blue-500 p-4 rounded">
              <p className="text-sm text-[#595d72] italic">
                💡 Once we understand your specific requirements, we'll recommend the most suitable solution for your business.
              </p>
            </div>
          </div>

          {/* Branding Packages */}
          <div>
            <h3 className="text-3xl font-bold text-[#152854] mb-4 text-center">Branding Packages <span className="text-lg font-normal text-[#595d72]">(Once-Off)</span></h3>
            <p className="text-center text-[#595d72] mb-8 max-w-3xl mx-auto text-lg">
              <strong>Branding is more than a logo.</strong> We design your entire visual identity—logo, colours, typography, and rules—so everything looks consistent everywhere.
            </p>
            
            <div className="grid md:grid-cols-3 gap-6 mb-6">
              {BRANDING_PACKAGES.map((pkg) => (
                <div key={pkg.id} className={`bg-white rounded-2xl shadow-lg p-8 border-2 ${pkg.popular ? 'border-[#dca851]' : 'border-gray-100'} hover:shadow-2xl transition-all duration-300 relative`}>
                  {pkg.popular && (
                    <div className="absolute top-0 right-0 bg-[#dca851] text-[#152854] px-4 py-1 rounded-bl-lg font-bold text-sm">
                      POPULAR
                    </div>
                  )}
                  <h4 className="text-2xl font-bold text-[#152854] mb-2">{pkg.name}</h4>
                  <div className="mb-4">
                    <span className="text-4xl font-bold text-[#dca851]">{pkg.price}</span>
                  </div>
                  <p className="text-sm text-[#595d72] mb-6 italic">Ideal for {pkg.idealFor}</p>
                  
                  <div className="space-y-3 mb-6">
                    {pkg.features.map((feature, j) => (
                      <div key={j} className="flex items-start space-x-2">
                        <Check className="text-[#dca851] flex-shrink-0 mt-0.5" size={18} />
                        <span className="text-sm text-[#595d72]">{feature}</span>
                      </div>
                    ))}
                  </div>

                  <a href={whatsappUrl(`Hi! I'm interested in the ${pkg.name}.`)} target="_blank" rel="noopener noreferrer" className="block w-full py-3 bg-[#152854] text-white text-center rounded-lg hover:bg-[#17294f] transition font-semibold">
                    WhatsApp for Branding
                  </a>
                </div>
              ))}
            </div>

            <div className="bg-amber-50 border-l-4 border-[#dca851] p-4 rounded">
              <p className="text-sm text-[#595d72]">
                <strong>⚠️ Important:</strong> Branding package ≠ logo only. Branding includes the full identity system from scratch.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-16 px-4 bg-white">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-[#152854] mb-4">How It Works</h2>
            <div className="w-24 h-1 bg-[#dca851] mx-auto"></div>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {[
              {
                step: '01',
                title: 'Message Us',
                description: 'Tell us about your business and goals on WhatsApp. Quick, easy, and personal.'
              },
              {
                step: '02',
                title: 'Get Your Plan',
                description: 'We recommend the best package and confirm timeline. No pressure, just clarity.'
              },
              {
                step: '03',
                title: 'Launch & Grow',
                description: 'We deliver, launch, and support your ongoing growth. You focus on your business.'
              }
            ].map((item, i) => (
              <div key={i} className="relative text-center">
                <div className="text-6xl font-bold text-[#dca851]/20 mb-4">{item.step}</div>
                <h3 className="text-2xl font-bold text-[#152854] mb-3">{item.title}</h3>
                <p className="text-[#595d72] leading-relaxed">{item.description}</p>
                {i < 2 && (
                  <div className="hidden md:block absolute top-12 -right-12 text-[#dca851]">
                    <ChevronDown className="rotate-[-90deg]" size={24} />
                  </div>
                )}
              </div>
            ))}
          </div>

          <div className="text-center mt-12">
            <a href={whatsappUrl()} target="_blank" rel="noopener noreferrer" className="inline-flex items-center space-x-2 px-8 py-4 bg-[#dca851] text-[#152854] rounded-lg hover:bg-[#d7ab61] transition font-bold text-lg shadow-lg">
              <MessageCircle size={24} />
              <span>Start Your Journey</span>
            </a>
          </div>
        </div>
      </section>

      {/* Testimonial */}
      <section id="reviews" className="py-20 px-4 bg-gradient-to-b from-[#f5f1f6] to-white">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-[#152854] mb-4">What Our Clients Say</h2>
            <div className="w-24 h-1 bg-[#dca851] mx-auto"></div>
          </div>

          <div className="bg-white rounded-2xl shadow-2xl p-8 md:p-12 border-2 border-[#dca851]/20">
            <div className="flex items-center justify-center mb-6">
              {[...Array(5)].map((_, i) => (
                <Star key={i} className="text-[#dca851] fill-current" size={24} />
              ))}
            </div>

            <div className="mb-8">
              {TESTIMONIAL.text.split('\n\n').map((para, i) => (
                <p key={i} className="text-[#595d72] leading-relaxed mb-4 text-center md:text-left">
                  {para}
                </p>
              ))}
            </div>

            <div className="border-t-2 border-[#dca851]/20 pt-6 text-center">
              <p className="font-bold text-[#152854] text-lg">{TESTIMONIAL.author}</p>
              <p className="text-[#595d72]">{TESTIMONIAL.position}</p>
              <a
                href={TESTIMONIAL.websiteUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="mt-2 inline-block text-[#152854] font-medium underline decoration-[#dca851] underline-offset-4 hover:text-[#dca851] transition"
              >
                {TESTIMONIAL.websiteUrl}
              </a>
            </div>
          </div>

          <div className="text-center mt-8">
            <a href={whatsappUrl("Hi! I saw the great review and I'm interested in your services.")} target="_blank" rel="noopener noreferrer" className="inline-flex items-center space-x-2 text-[#152854] font-semibold hover:text-[#dca851] transition">
              <span>Want results like this? WhatsApp us.</span>
              <ChevronDown className="rotate-[-90deg]" size={18} />
            </a>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section id="faq" className="py-20 px-4 bg-white">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-[#152854] mb-4">Frequently Asked Questions</h2>
            <div className="w-24 h-1 bg-[#dca851] mx-auto"></div>
          </div>

          <div className="space-y-4">
            {FAQS.map((faq, i) => (
              <div key={i} className="bg-[#f5f1f6] rounded-xl overflow-hidden border-2 border-transparent hover:border-[#dca851]/30 transition">
                <button
                  onClick={() => setExpandedFaq(expandedFaq === i ? null : i)}
                  className="w-full px-6 py-4 flex justify-between items-center text-left"
                >
                  <span className="font-semibold text-[#152854] pr-4">{faq.q}</span>
                  <ChevronDown className={`text-[#dca851] flex-shrink-0 transition-transform ${expandedFaq === i ? 'rotate-180' : ''}`} size={20} />
                </button>
                {expandedFaq === i && (
                  <div className="px-6 pb-4">
                    <p className="text-[#595d72] leading-relaxed">{faq.a}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact / CTA */}
      <section id="contact" className="py-20 px-4 bg-gradient-to-br from-[#152854] via-[#17294f] to-[#152854] text-white">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-4xl md:text-5xl font-bold mb-6">Let's Grow Your Business</h2>
          <p className="text-xl text-white/90 mb-10 max-w-2xl mx-auto">
            Ready to take your small business to the next level? Get in touch today and let's create something amazing together.
          </p>

          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center mb-8">
            <a href={whatsappUrl()} target="_blank" rel="noopener noreferrer" className="w-full sm:w-auto px-8 py-4 bg-[#dca851] text-[#152854] rounded-lg hover:bg-[#d7ab61] transition font-bold text-lg flex items-center justify-center space-x-2 shadow-2xl transform hover:scale-105">
              <MessageCircle size={24} />
              <span>WhatsApp Us</span>
            </a>
            <a href={emailUrl()} className="w-full sm:w-auto px-8 py-4 border-2 border-white text-white rounded-lg hover:bg-white/10 transition font-semibold text-lg flex items-center justify-center space-x-2">
              <Mail size={24} />
              <span>Email Us</span>
            </a>
          </div>

          <div className="space-y-2 text-white/80">
            <p className="flex items-center justify-center space-x-2">
              <MessageCircle size={18} />
              <a href={whatsappUrl()} target="_blank" rel="noopener noreferrer" className="hover:text-[#dca851] transition">
                {CONTACT.whatsapp}
              </a>
            </p>
            <p className="flex items-center justify-center space-x-2">
              <Mail size={18} />
              <a href={emailUrl()} className="hover:text-[#dca851] transition">
                {CONTACT.email}
              </a>
            </p>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-[#152854] text-white/60 py-8 px-4 border-t border-white/10">
        <div className="max-w-6xl mx-auto text-center">
          <div className="flex justify-center mb-4">
            <img src="/brandlift-logo.jpg" alt="BrandLift Marketing" className="h-16 w-auto max-w-[210px] object-contain" loading="lazy" />
          </div>
          <p className="text-sm">© 2025 BrandLift Marketing. Affordable marketing solutions for small businesses.</p>
        </div>
      </footer>

      {/* Floating WhatsApp Button */}
      <a
        href={whatsappUrl()}
        target="_blank"
        rel="noopener noreferrer"
        className="fixed bottom-6 right-6 w-16 h-16 bg-[#25D366] text-white rounded-full shadow-2xl flex items-center justify-center hover:scale-110 transition-transform z-50 animate-pulse"
        aria-label="Contact us on WhatsApp"
      >
        <MessageCircle size={32} />
      </a>
    </div>
  );
}
